
<div class="profile-search-view">
  <ftu-block
    dismiss-key="profiles"
    icon="badge"
    title="This is the Profile Search"
    description="Profile Search allows you to search the millions of cards in our database organized by the multiple grading companies and presented as profiles. Many of these profiles have been matched together to create a single profile for all grading companies. This page exists to allow you to find all the cards that exist out there and research their population reports and sales histories."
  />

  <page-header title="Profile Search">
    <template #actions>
      <align
        v-if="isStaff && (
          selectedProfilesForMatching.length === 1
          || canMatchProfiles
          || selectedMatchedProfiles.length >= 1
        )"
        small-gap
        class="match-actions"
      >
        <icon-button
          v-if="selectedProfilesForMatching.length === 1 && !selectedMatchedProfiles.length"
          icon="visibility_off"
          tooltip="Exclude this profile from the unmatched match queue"
          :loading="excludingFromMatchQueue"
          @click="excludeSelectedFromMatchQueue"
        />
        <btn
          v-if="selectedMatchedProfiles.length >= 1 && !canMatchProfiles"
          :loading="unmatching"
          loading-text="Unmatching..."
          @click="unmatchProfiles"
        >
          Unmatch {{ selectedMatchedProfiles.length }} profile(s)
        </btn>
        <template v-if="canMatchProfiles">
          <checkbox
            :value="findSetMatches"
            label="Find Set Matches"
            @input="findSetMatches = $event"
          />
          <btn
            :loading="matching"
            loading-text="Matching..."
            @click="matchProfiles"
          >
            Match {{ profilesForMerge.length }} profile(s)
          </btn>
        </template>
      </align>
    </template>
  </page-header>

  <align
    flex
    collapse-end
    direction="flex-start"
    class="search-row"
  >
    <shared-filters
      :filters.sync="filters"
      :query.sync="query"
      :properties="defaultFilters"
      placeholder="Search profiles..."
      @clear="clearFilters"
    />
  </align>

  <filter-results
    :is-filtering="isFiltering"
    :filters="filters"
    :results="numResults != null && numResults < 10000 ? numResults : null"
    @clear="clearFilters"
  />

  <div v-if="isStaff && profilesForMerge.length" class="selected-profiles-strip">
    <div class="selected-profiles-strip-label">
      Selected for matching ({{ profilesForMerge.length }})
    </div>
    <div class="selected-profiles-strip-list">
      <div
        v-for="p in profilesForMerge"
        :key="p.id"
        class="selected-profile-pill"
        :class="{ 'selected-profile-pill--panel-active': isOpenInSidePanel(p.id) }"
      >
        <router-link
          :to="getProfilePanelLocation(p)"
          class="selected-profile-pill-link"
        >
          <span class="selected-profile-pill-name">{{ p.label || p.id || 'Profile' }}</span>
          <span class="selected-profile-pill-graders">{{ gradersParenthetical(p) }}</span>
        </router-link>
        <button
          type="button"
          class="selected-profile-pill-remove"
          aria-label="Remove from selection"
          @click="setProfileSelected(p, false)"
        >
          ×
        </button>
      </div>
    </div>
  </div>

  <div class="results">
    <empty-state
      v-if="showEmptyState"
      icon="search_off"
      title="There are no results for your query"
      description="Try searching for something else"
    />

    <template v-else>
      <div class="list">
        <div
          v-for="(item, i) in visibleResults"
          :key="item.id || i"
          class="profile-card-wrapper"
        >
          <checkbox
            v-if="isStaff"
            :value="selectedProfileIds.includes(item.id)"
            class="profile-checkbox"
            @input="setProfileSelected(item, $event)"
          />

          <router-link
            :to="getProfilePanelLocation(item)"
            class="profile-card"
            :class="{ 'profile-card--panel-active': isOpenInSidePanel(item.id) }"
          >
            <align
              small-gap
              wrap
              direction="center"
              class="profile-card-row"
            >
              <span class="profile-card-label">
                {{ item.label || item.id || 'No label' }}
              </span>

              <pill
                v-for="grader in getGraders(item)"
                :key="grader"
                readonly
                no-margin
              >
                {{ grader }}
              </pill>

              <span v-if="popValue(item) != null" class="pop">
                Pop: {{ $filters.number(popValue(item)) }}
              </span>
            </align>
          </router-link>
        </div>
      </div>

      <infinite-scroll
        v-if="results.length > 0"
        :loading="loading"
        :has-more-data="hasMoreData"
        @load="nextPage"
      />
    </template>
  </div>

  <scroll-top />

  <div
    v-if="loadingSetSuggestions"
    class="set-suggestions-loading-overlay"
  >
    <spinner inline />
    <span class="set-suggestions-loading-text">Checking for set matches...</span>
  </div>

  <profile-set-matches-modal
    :open="showSetMatchesModal"
    :suggested-set-matches="suggestedSetMatches"
    :profile-info="setMatchProfileInfo"
    @close="onSetMatchesModalClose"
  />
</div>
